import type { EnvironmentFootprint } from "../environment-footprint";
import {
	type HexagonLayoutMetrics,
	hexCellPosition,
} from "./hexagon-layout-metrics";

export interface EnvironmentFootprintPixelBounds {
	left: number;
	top: number;
	width: number;
	height: number;
	centerX: number;
	centerY: number;
}

export function computeEnvironmentFootprintPixelBounds(params: {
	anchorRow: number;
	anchorCol: number;
	footprint: EnvironmentFootprint;
	metrics: HexagonLayoutMetrics;
}): EnvironmentFootprintPixelBounds {
	const { anchorRow, anchorCol, footprint, metrics } = params;
	let minLeft = Number.POSITIVE_INFINITY;
	let minTop = Number.POSITIVE_INFINITY;
	let maxRight = Number.NEGATIVE_INFINITY;
	let maxBottom = Number.NEGATIVE_INFINITY;

	for (let rowOffset = 0; rowOffset < footprint.height; rowOffset += 1) {
		for (let colOffset = 0; colOffset < footprint.width; colOffset += 1) {
			const position = hexCellPosition(
				anchorRow + rowOffset,
				anchorCol + colOffset,
				metrics,
			);
			minLeft = Math.min(minLeft, position.left);
			minTop = Math.min(minTop, position.top);
			maxRight = Math.max(maxRight, position.left + metrics.hexagonWidth);
			maxBottom = Math.max(
				maxBottom,
				position.top + metrics.hexagonHeight,
			);
		}
	}

	if (!Number.isFinite(minLeft) || !Number.isFinite(minTop)) {
		const fallback = hexCellPosition(anchorRow, anchorCol, metrics);
		const width = metrics.hexagonWidth;
		const height = metrics.hexagonHeight;
		return {
			left: fallback.left,
			top: fallback.top,
			width,
			height,
			centerX: fallback.left + width / 2,
			centerY: fallback.top + height / 2,
		};
	}

	const width = Math.max(metrics.hexagonWidth, maxRight - minLeft);
	const height = Math.max(metrics.hexagonHeight, maxBottom - minTop);
	return {
		left: minLeft,
		top: minTop,
		width,
		height,
		centerX: minLeft + width / 2,
		centerY: minTop + height / 2,
	};
}

export function resolveEnvironmentSpriteDimensions(params: {
	bounds: EnvironmentFootprintPixelBounds;
	aspectRatio?: number;
	minSize?: number;
}): { width: number; height: number } {
	const { bounds, aspectRatio, minSize = 28 } = params;
	const containerWidth = Math.max(minSize, bounds.width);
	const containerHeight = Math.max(minSize, bounds.height);
	const resolvedAspectRatio =
		aspectRatio && aspectRatio > 0
			? aspectRatio
			: containerWidth / Math.max(containerHeight, 1);

	if (containerWidth / containerHeight >= resolvedAspectRatio) {
		const height = containerHeight;
		return {
			width: Math.max(minSize, Math.round(height * resolvedAspectRatio)),
			height,
		};
	}

	const width = containerWidth;
	return {
		width,
		height: Math.max(minSize, Math.round(width / resolvedAspectRatio)),
	};
}
